<template>
  <TasksCard
    v-model="show"
    v-model:expanded="expanded"
    :btn-close-props="{
      disabled: isSomeTaskUploadItemProcessing,
      loading: !expanded && isSomeTaskUploadItemProcessing,
    }"
    :max-height="CARD_MAX_HEIGHT"
    @click:close="toggleShow"
    @click:expanded="expanded = !expanded"
    @mouseleave="onMouseLeave"
    @mouseover="cancelExpandCloseTimeout"
  >
    <template #title>
      <span class="mx-1 text-body-large font-weight-thin">
        {{ t('nodes.upload_card.title') }}
      </span>

      <v-defaults-provider :defaults="{ VIcon: { size: '15', class: 'ml-2' } }">
        <v-chip
          v-if="!expanded && taskUploadsCount > 0"
          :append-icon="taskUploadsStateIcon"
          class="mx-2"
          :color="taskUploadsStateColor"
          size="small"
        >
          <span
            v-if="
              taskUploadsCountProcessing > 0 ||
              taskUploadsCountCompleted === taskUploadsCount
            "
          >
            {{ taskUploadsCountCompleted }}/{{ taskUploadsCount }}
          </span>
          <span v-else-if="taskUploadsCountWarning + taskUploadsCountError > 0">
            {{ taskUploadsCountWarning + taskUploadsCountError }}
          </span>
          <span v-else>{{ taskUploadsCount }}</span>
        </v-chip>
      </v-defaults-provider>
    </template>

    <template #content>
      <!-- FILTERS -->
      <template v-if="filtersAvailable.length > 1">
        <v-divider />

        <v-chip-group v-model="currentFilter" class="ma-1 mx-4" column>
          <v-chip
            v-for="filter in filtersAvailable"
            :key="filter.value"
            :color="filter.color ?? 'primary'"
            density="compact"
            filter
            :text="filter.text"
            :value="filter.value"
            variant="outlined"
          >
            <template
              v-if="filter.value === 'error' && taskUploadsCountError > 0"
              #append
            >
              <v-icon color="warning" end size="small">
                far fa-triangle-exclamation
              </v-icon>
            </template>
          </v-chip>
        </v-chip-group>
      </template>

      <v-divider />

      <v-list-item v-if="taskUploadItemsFiltered.length > 0">
        <template #title>
          <span data-test-id="total-files">
            {{
              t('nodes.upload_card.labels.count_n_of_total', {
                n: currentFilter
                  ? getTaskUploadsByStatus(currentFilter as TaskUploadStatus)
                      .length
                  : taskUploadsCount - taskUploadsNotDoneYetCount,
                total: taskUploadsCount,
              })
            }}
          </span>
        </template>
        <template #append>
          <span
            class="text-grey text-body-small mx-2"
            data-test-id="total-files-size"
          >
            {{ humanFileSize(taskUploadTotalSizeUploaded) }}
            /
            {{ humanFileSize(taskUploadsTotalSize) }}
          </span>
          <DeepMenu
            :context="taskUpload"
            :items="taskUploadsMenuItems"
            :list-props="{
              density: 'compact',
            }"
          >
            <template #activator="{ props: ActivatorProps }">
              <v-btn
                v-bind="ActivatorProps"
                :disabled="
                  taskUploadsMenuItems.filter(
                    (i) => i.show?.(taskUpload) ?? true,
                  ).length === 0
                "
                icon="far fa-ellipsis-vertical"
                name="btn-task-upload-error-actions"
                size="x-small"
                slim
                variant="text"
              ></v-btn>
            </template>
          </DeepMenu>
        </template>
      </v-list-item>

      <v-divider />

      <v-empty-state
        v-if="taskUploadItemsFiltered.length === 0"
        icon="far fa-cloud-check"
        :text="t('upload.no_items')"
      />

      <v-list
        v-else
        class="py-0 overflow-y-auto"
        density="compact"
        :height="xs ? undefined : CARD_MAX_HEIGHT - 150"
        slim
      >
        <v-list-item
          v-for="taskUploadItem in taskUploadItemsFiltered.slice().reverse()"
          :key="`taskUpload-${taskUploadItem.id}`"
          :title="getTaskUploadTitle(taskUploadItem)"
        >
          <template #title>
            <span
              v-tooltip="{
                text: getTaskUploadTitle(taskUploadItem),
                openDelay: 300,
              }"
              data-test-id="filename"
            >
              {{ getTaskUploadTitle(taskUploadItem) }}
            </span>
          </template>

          <template #subtitle>
            <div class="d-flex justify-space-between">
              <span v-if="taskUploadItem.status === 'error'" class="text-error">
                {{ getTaskUploadErrorMessage(taskUploadItem) }}
              </span>
              <div v-else data-test-id="filesize">
                <span v-if="taskUploadItem.status === 'processing'">
                  {{
                    humanFileSize(
                      getTaskUploadFilesSizeUploaded(taskUploadItem),
                    )
                  }}
                  /
                  {{ humanFileSize(getTaskUploadFilesSize(taskUploadItem)) }}
                </span>
                <span v-else>
                  {{ getTaskUploadTotalSizeDisplay(taskUploadItem) }}
                </span>
              </div>
            </div>
          </template>

          <!-- Prepend: folder/file icon -->
          <template #prepend>
            <v-avatar
              v-if="getTaskUploadIconNode(taskUploadItem)"
              size="20"
              tile
              @click="openNodeDialog(taskUploadItem)"
            >
              <NodeIcon
                :mime-type="getTaskUploadIconNode(taskUploadItem)?.mimeType"
                :name="getTaskUploadIconNode(taskUploadItem)?.name"
                :type="getTaskUploadIconNode(taskUploadItem)?.type"
              />
            </v-avatar>
            <v-icon
              v-else
              v-bind="getTaskUploadIcon(taskUploadItem)"
              @click="openNodeDialog(taskUploadItem)"
            />
          </template>

          <!-- Append: progress / status / chip -->
          <template #append>
            <v-chip
              v-if="getTaskUploadItemsCountTotal(taskUploadItem) > 1"
              class="pt-1"
              size="small"
              variant="text"
            >
              {{ getTaskUploadItemsCountWithCompleted(taskUploadItem) }}/{{
                getTaskUploadItemsCountTotal(taskUploadItem)
              }}
            </v-chip>

            <v-icon
              v-if="['added', 'processing'].includes(taskUploadItem.status)"
              v-tooltip="t(`upload.status.${taskUploadItem.status}`)"
              class="mx-2"
            >
              <v-progress-circular
                :indeterminate="
                  taskUploadItem.status === 'processing' &&
                  !getTaskUploadItemProgress(taskUploadItem)
                "
                :model-value="
                  getTaskUploadItemProgress(taskUploadItem) || undefined
                "
                :rotate="
                  getTaskUploadItemProgress(taskUploadItem) ? 360 : undefined
                "
                width="2"
              />
            </v-icon>

            <v-icon
              v-else-if="
                (
                  [
                    TASK_UPLOAD_STATUS.ERROR,
                    TASK_UPLOAD_STATUS.WARNING,
                  ] as TaskUploadStatus[]
                ).includes(taskUploadItem.status)
              "
              v-bind="getTaskUploadStateIcon(taskUploadItem.status)"
              class="mx-2"
              @click="openNodeDialog(taskUploadItem)"
            ></v-icon>

            <v-hover v-else v-slot="{ isHovering, props: HoverProps }">
              <div v-bind="HoverProps">
                <div v-if="['canceled'].includes(taskUploadItem.status)">
                  <v-chip
                    density="compact"
                    @click="
                      isHovering
                        ? onTaskUploadItemSetStatus('added', taskUploadItem)
                        : undefined
                    "
                  >
                    <span v-if="isHovering">
                      {{ t('actions.try_again') }}
                    </span>
                    <span v-else>
                      {{ t(`upload.status.${taskUploadItem.status}`) }}
                    </span>
                  </v-chip>
                </div>
                <v-btn
                  v-else-if="isHovering && taskUploadItem.metadata?.route"
                  v-tooltip="{ text: t('tooltip.open'), openDelay: 300 }"
                  v-bind="HoverProps"
                  data-test-id="go-to-file"
                  :icon="
                    isHovering
                      ? 'far fa-folder'
                      : getTaskUploadStateIcon(taskUploadItem.status)?.icon
                  "
                  size="x-small"
                  slim
                  :to="taskUploadItem.metadata?.route"
                  variant="plain"
                />
                <v-icon
                  v-else
                  v-tooltip="{
                    text: t(`upload.status.${taskUploadItem.status}`),
                    openDelay: 300,
                  }"
                  v-bind="{
                    ...HoverProps,
                    ...getTaskUploadStateIcon(taskUploadItem.status),
                  }"
                  class="mx-2"
                  :data-test-id="`upload-${taskUploadItem.status}`"
                ></v-icon>
              </div>
            </v-hover>
            <DeepMenu
              :context="taskUploadItem"
              :items="taskUploadMenuItems"
              :list-props="{
                density: 'compact',
              }"
            >
              <template #activator="{ props: ActivatorProps }">
                <v-btn
                  v-bind="ActivatorProps"
                  :disabled="
                    taskUploadMenuItems.filter(
                      (i) => i.show?.(taskUploadItem) ?? true,
                    ).length === 0
                  "
                  icon="far fa-ellipsis-vertical"
                  name="btn-task-upload-error-actions"
                  size="x-small"
                  slim
                  variant="text"
                ></v-btn>
              </template>
            </DeepMenu>
          </template>
        </v-list-item>
      </v-list>

      <TaskUploadsDialog
        v-model="showNodeUploadDialog"
        :task-upload="currentTaskUploadItem"
        @click:file-delete="onClickFileDelete"
        @click:file-retry="onClickFileRetry"
        @click:folder-retry="onClickFolderRetry"
      />
    </template>
  </TasksCard>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify/framework'
import { DeepMenu } from '@deepcloud/deep-ui-lib'
import { humanFileSize } from '@deepcloud/deep-ui-utils'
import NodeIcon from '@/components/node/NodeIcon.vue'
import TasksCard from '@/components/tasks/TasksCard.vue'
import TaskUploadsDialog from '@/components/upload/components/TaskUploadsDialog.vue'
import {
  getTaskUploadFilesSize,
  getTaskUploadFilesSizeUploaded,
  useTaskUpload,
  useTaskUploads,
} from '../composables'
import { TASK_UPLOAD_STATUS } from '../types'
import type { TaskUploadDeepBox } from '@/components/upload-deepbox'
import type { MenuItem } from '@deepcloud/deep-ui-lib'
import type { TaskUpload, TaskUploadNode, TaskUploadStatus } from '../types'

const tasksUpload = defineModel<TaskUploadDeepBox[]>('task-upload', {
  default: () => [],
})

const { t } = useI18n()
const { xs } = useDisplay()

const {
  getTaskUploadIconNode,
  getTaskUploadStateIcon,
  getTaskUploadTotalSizeDisplay,
  getTaskUploadErrorMsgFormatted,
  getTaskUploadItemsFoldersCount,
  onTaskUploadItemSetStatus,
} = useTaskUpload()

const {
  taskUploadsCount,
  taskUploadsCountCompleted,
  taskUploadsCountError,
  taskUploadsCountWarning,
  taskUploadTotalSizeUploaded,
  taskUploadsTotalSize,
  taskUploadsNotDoneYetCount,
  getTaskUploadsByStatus,
  taskUploadsStateIcon,
  taskUploadsStateColor,
  taskUploadsCountProcessing,
  isSomeTaskUploadItemProcessing,
} = useTaskUploads(tasksUpload)

const CARD_MAX_HEIGHT = 350

const show = ref(false)
const expanded = ref(false)
const showNodeUploadDialog = ref(false)
const currentTaskUploadItem = ref<TaskUpload>()

const currentFilter = ref<TaskUploadStatus | string>('')

const filtersAvailable = computed(() => {
  const filters = []

  const presentStatuses = new Set(tasksUpload.value.map((t) => t.status))

  const filterStatus = Object.values(TASK_UPLOAD_STATUS)
    .filter((f) => !['added', 'processing', 'warning'].includes(f))
    .filter((f) => presentStatuses.has(f))
    .map((f) => ({
      value: f,
      text: t(`upload.status.${f}`),
      color: getTaskUploadStateIcon(f)?.color,
    }))

  return [...filters, ...filterStatus]
})

watch(
  () => filtersAvailable.value.length,
  () => {
    currentFilter.value = ''
  },
)

const taskUploadItemsFiltered = computed(() =>
  currentFilter.value
    ? (getTaskUploadsByStatus(currentFilter.value) as TaskUploadDeepBox[])
    : (tasksUpload.value as TaskUploadDeepBox[]),
)

function openNodeDialog(taskUpload: TaskUpload) {
  currentTaskUploadItem.value = taskUpload
  showNodeUploadDialog.value = true
}

function clearTaskUploads() {
  tasksUpload.value = []
}

function toggleShow() {
  show.value = !show.value
  if (!show.value && !isSomeTaskUploadItemProcessing.value) {
    clearTaskUploads()
  }
}

const isExpandCloseWaiting = ref(false)
const expandCloseTimeout = ref<ReturnType<typeof setTimeout>>()

function cancelExpandCloseTimeout() {
  clearTimeout(expandCloseTimeout.value)
}

function startExpandCloseTimeout() {
  cancelExpandCloseTimeout()
  expandCloseTimeout.value = setTimeout(() => {
    expanded.value = false
    isExpandCloseWaiting.value = false
  }, 5000)
}

function onMouseLeave() {
  if (isExpandCloseWaiting.value) startExpandCloseTimeout()
}

watch(
  () => isSomeTaskUploadItemProcessing.value,
  (val) => {
    if (!val && show.value) {
      isExpandCloseWaiting.value = true
      startExpandCloseTimeout()
    } else {
      cancelExpandCloseTimeout()
    }
  },
)
watch(
  () => taskUploadsCount.value,
  (val) => {
    show.value = val > 0
    expanded.value = val > 0
  },
  { immediate: true },
)

function getTaskUploadIcon(taskUpload: TaskUpload) {
  const foldersCount = getTaskUploadItemsFoldersCount(taskUpload)
  if (foldersCount > 0) {
    return {
      icon: 'far fa-folder-arrow-up',
    }
  }
  return {
    icon: 'far fa-upload',
  }
}

const route = useRoute()
watch(
  () => route.name,
  (newValue) => {
    if (newValue && !isSomeTaskUploadItemProcessing.value) {
      expanded.value = false
    }
  },
)

function getTaskUploadTitle(taskUpload: TaskUpload) {
  if (taskUpload.folders && Object.keys(taskUpload.folders).length > 0)
    return Object.values(taskUpload.folders)[0].data?.name
  if (taskUpload.files && taskUpload.files.length > 0)
    return (
      taskUpload.files[0].data?.file?.name || taskUpload.files[0].data?.name
    )
  return ''
}

function getTaskUploadItemsCountTotal(taskUpload: TaskUpload) {
  return (
    (taskUpload.folders ? Object.keys(taskUpload.folders).length : 0) +
    (taskUpload.files ? taskUpload.files.length : 0)
  )
}
function getTaskUploadItemsCountWithCompleted(taskUpload: TaskUpload) {
  const folders = taskUpload.folders
    ? Object.values(taskUpload.folders).filter((f) => f.status === 'completed')
        .length
    : 0
  const files = taskUpload.files
    ? taskUpload.files.filter((f) => f.status === 'completed').length
    : 0
  return folders + files
}

function getTaskUploadItemProgress(taskUpload: TaskUpload) {
  return (
    (100 * getTaskUploadItemsCountWithCompleted(taskUpload)) /
    getTaskUploadItemsCountTotal(taskUpload)
  )
}

function getTaskUploadErrorMessage(taskUpload: TaskUpload) {
  if (taskUpload.folders && Object.keys(taskUpload.folders).length === 1)
    return getTaskUploadErrorMsgFormatted(
      Object.values(taskUpload.folders)[0].data?.error,
    )
  if (taskUpload.files && taskUpload.files.length === 1)
    return getTaskUploadErrorMsgFormatted(taskUpload.files[0].data?.error)
  return ''
}

onUnmounted(() => {
  if (!isSomeTaskUploadItemProcessing.value) {
    clearTaskUploads()
  }
})

const taskUploadMenuItems = computed((): MenuItem[] => [
  {
    id: 'task-upload-open',
    title: t('actions.open_details'),
    prepend: {
      icon: {
        icon: 'far fa-eye',
        size: 'x-small',
      },
    },
    show: () => true,
    action: (taskUploadItem: TaskUploadDeepBox) => {
      openNodeDialog(taskUploadItem)
    },
  },
  {
    id: 'task-upload-try-again',
    title: t('actions.try_again'),
    prepend: {
      icon: {
        icon: 'far fa-arrows-repeat',
        size: 'x-small',
      },
    },
    show: (taskUploadItem: TaskUpload) =>
      taskUploadItem.status === 'canceled' || taskUploadItem.status === 'error',
    action: (taskUploadItem: TaskUpload) =>
      onTaskUploadItemSetStatus('added', taskUploadItem),
  },
  {
    id: 'task-upload-cancel',
    title: t('actions.cancel'),
    prepend: {
      icon: {
        icon: 'far fa-cancel',
        size: 'x-small',
      },
    },
    show: (taskUploadItem: TaskUpload) =>
      taskUploadItem.status === 'added' ||
      taskUploadItem.status === 'processing',
    action: (taskUploadItem: TaskUpload) =>
      onTaskUploadItemSetStatus('canceled', taskUploadItem),
  },
  {
    id: 'task-upload-delete',
    title: t('actions.remove'),
    prepend: {
      icon: {
        icon: 'far fa-trash',
        color: 'error',
        size: 'x-small',
      },
    },
    show: (taskUploadItem: TaskUpload) =>
      taskUploadItem.status !== 'processing',
    action: (taskUploadItem: TaskUpload) => {
      tasksUpload.value = tasksUpload.value.filter(
        (p) => p.id !== taskUploadItem.id,
      )
    },
  },
])

function onClickFolderRetry({
  taskUpload,
}: {
  taskUpload: TaskUpload
  folder: TaskUploadNode
}) {
  onTaskUploadItemSetStatus('added', taskUpload)
}

function onClickFileRetry({
  taskUpload,
}: {
  taskUpload: TaskUpload
  file: TaskUploadNode
}) {
  onTaskUploadItemSetStatus('added', taskUpload)
}

function onClickFileDelete({
  taskUpload,
  file,
}: {
  taskUpload: TaskUpload
  file: TaskUploadNode
}) {
  if (!taskUpload?.files?.length) return
  const fileIdx = taskUpload.files.findIndex((f) => f.id === file.id)
  if (fileIdx > -1) {
    taskUpload.files.splice(fileIdx, 1)
  }

  const taskUploadIdx = tasksUpload.value.findIndex((p) => p.id === p.id)
  if (taskUploadIdx > -1) {
    tasksUpload.value[taskUploadIdx].files = taskUpload.files
  }
}

const taskUploadsMenuItems = computed((): MenuItem[] => [
  {
    id: 'task-upload-try-again',
    title: t('actions.try_again'),
    prepend: {
      icon: {
        icon: 'far fa-arrows-repeat',
        size: 'x-small',
      },
    },
    show: (tu: TaskUpload[]) =>
      tu.some((i) => i.status === 'canceled' || i.status === 'error'),
    action: (tu: TaskUpload[]) => {
      tu.forEach((i) => {
        onTaskUploadItemSetStatus('added', i)
      })
    },
  },
  {
    id: 'task-upload-cancel',
    title: t('actions.cancel'),
    prepend: {
      icon: {
        icon: 'far fa-cancel',
        size: 'x-small',
      },
    },
    show: (tu: TaskUpload[]) =>
      tu.some((i) => i.status === 'added' || i.status === 'processing'),
    action: (tu: TaskUpload[]) => {
      tu.forEach((i) => {
        onTaskUploadItemSetStatus('canceled', i)
      })
    },
  },
])
</script>
